/**
 * /profile/edit — Full LinkedIn-style profile editor.
 *
 * Tabs:
 *   Basics · Photos · Education · Career · Skills · Achievements · Privacy
 *
 * Basics owns: handle, name, gender, year_of_birth (+private toggle),
 *              profession, avatar, website + social URLs, about (3000 char),
 *              general_notes.
 *
 * The four list sections (education/career/skills/achievements) are managed
 * as ordered arrays with add / edit / delete inline.
 *
 * All text areas get an "✨ Improve with AI" button that calls the backend.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http, API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Save, Trash2, Plus, User as UserIcon, GraduationCap, Briefcase,
  Sparkles, Award, Image as ImageIcon, Shield, AtSign, Camera,
  ChevronLeft, Loader2, Facebook, Instagram, Youtube, Linkedin, Music2, Globe,
} from "lucide-react";
import Avatar from "@/components/Avatar";
import AIImproveButton from "@/components/AIImproveButton";
import LinkIdentities from "@/components/LinkIdentities";

const TABS = [
  { key: "basics", label: "Basics", Icon: UserIcon },
  { key: "photos", label: "Photos", Icon: ImageIcon },
  { key: "education", label: "Education", Icon: GraduationCap },
  { key: "career", label: "Career", Icon: Briefcase },
  { key: "skills", label: "Skills", Icon: Sparkles },
  { key: "achievements", label: "Achievements", Icon: Award },
  { key: "signin", label: "Sign-in", Icon: AtSign },
  { key: "privacy", label: "Privacy", Icon: Shield },
];

const SOCIALS = [
  { key: "website_url", label: "Website", Icon: Globe },
  { key: "linkedin_url", label: "LinkedIn", Icon: Linkedin },
  { key: "youtube_url", label: "YouTube", Icon: Youtube },
  { key: "facebook_url", label: "Facebook", Icon: Facebook },
  { key: "instagram_url", label: "Instagram", Icon: Instagram },
  { key: "twitter_url", label: "X / Twitter", Icon: () => <span style={{ fontFamily: "serif" }}>𝕏</span> },
  { key: "tiktok_url", label: "TikTok", Icon: Music2 },
];

export default function ProfileEdit() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("basics");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        // /me/profile returns the fresh full user (private fields incl.)
        const { data } = await http.get("/me/profile");
        setMe(data.user || {});
      } catch (e) {
        toast.error("Couldn't load your profile.");
      } finally { setLoading(false); }
    })();
  }, [user]);

  if (!user) return <div className="max-w-6xl mx-auto px-6 py-24 text-center" style={{ color: "var(--wofy-text-soft)" }}>Please sign in to edit your profile.</div>;
  if (loading || !me) return <div className="max-w-6xl mx-auto px-6 py-24 text-center inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10" data-testid="profile-edit-page">
      <Link to="/profile" className="inline-flex items-center gap-2 text-sm hover:underline mb-4" style={{ color: "var(--wofy-primary)" }}>
        <ChevronLeft className="w-4 h-4" /> Back to my profile
      </Link>
      <div className="overline">SOCIAL PULSE · MY PROFILE</div>
      <h1 className="font-display text-4xl sm:text-5xl mt-2">Edit your profile</h1>
      <p className="mt-3 text-sm max-w-2xl" style={{ color: "var(--wofy-text-soft)" }}>
        Complete every section to unlock the Members directory, resume export, and connections. Add photos, education, career, skills and achievements — just like LinkedIn.
      </p>

      {/* Tabs */}
      <div className="mt-8 flex flex-wrap gap-1 border-b" style={{ borderColor: "var(--wofy-border)" }}>
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`profile-tab-${t.key}`}
              className="px-4 py-3 -mb-px text-sm font-medium flex items-center gap-2 border-b-2 transition-colors"
              style={active
                ? { borderColor: "var(--wofy-primary)", color: "var(--wofy-primary)" }
                : { borderColor: "transparent", color: "var(--wofy-text-soft)" }
              }
            >
              <t.Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      <div className="mt-8">
        {tab === "basics" && <BasicsTab me={me} setMe={setMe} refresh={refresh} />}
        {tab === "photos" && <PhotosTab me={me} setMe={setMe} />}
        {tab === "education" && <ListSection kind="education" me={me} setMe={setMe} />}
        {tab === "career" && <ListSection kind="career" me={me} setMe={setMe} />}
        {tab === "skills" && <ListSection kind="skills" me={me} setMe={setMe} />}
        {tab === "achievements" && <ListSection kind="achievements" me={me} setMe={setMe} />}
        {tab === "signin" && <LinkIdentities />}
        {tab === "privacy" && <PrivacyTab me={me} setMe={setMe} />}
      </div>
    </div>
  );
}

// ---------------- BASICS ----------------

function BasicsTab({ me, setMe, refresh }) {
  const [form, setForm] = useState({
    name: me.name || "",
    handle: me.handle || "",
    gender: me.gender || "",
    year_of_birth: me.year_of_birth || "",
    year_of_birth_private: !!me.year_of_birth_private,
    profession: me.profession || "",
    about: me.about || "",
    general_notes: me.general_notes || "",
    website_url: me.website_url || me.website || "",
    linkedin_url: me.linkedin_url || "",
    youtube_url: me.youtube_url || "",
    facebook_url: me.facebook_url || "",
    instagram_url: me.instagram_url || "",
    twitter_url: me.twitter_url || "",
    tiktok_url: me.tiktok_url || "",
    profile_picture: me.profile_picture || me.avatar_url || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.gender) return toast.error("Gender is required.");
    if (!form.name?.trim()) return toast.error("Name is required.");
    setBusy(true);
    try {
      // 1. Update the legacy fields via existing /me/profile PATCH
      await http.patch("/me/profile", {
        name: form.name,
        profile_picture: form.profile_picture || null,
        website: form.website_url || null,
      });

      // 2. Update the LinkedIn-style extended fields
      await http.patch("/profile/extended", {
        gender: form.gender,
        year_of_birth: form.year_of_birth ? parseInt(form.year_of_birth, 10) : null,
        year_of_birth_private: form.year_of_birth_private,
        profession: form.profession || null,
        about: form.about || null,
        general_notes: form.general_notes || null,
        website_url: form.website_url || null,
        linkedin_url: form.linkedin_url || null,
        youtube_url: form.youtube_url || null,
      });

      // 3. Handle + socials on the "extended handle" endpoint (has uniqueness check).
      if (form.handle && form.handle !== me.handle) {
        try {
          await http.patch("/profiles/me", { handle: form.handle });
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Handle change failed");
        }
      }
      await http.patch("/profiles/me", {
        profession: form.profession || null,
        linkedin_url: form.linkedin_url || null,
        youtube_url: form.youtube_url || null,
        facebook_url: form.facebook_url || null,
        instagram_url: form.instagram_url || null,
        twitter_url: form.twitter_url || null,
        tiktok_url: form.tiktok_url || null,
        website: form.website_url || null,
        avatar_url: form.profile_picture || null,
      });

      toast.success("Profile saved.");
      const { data } = await http.get("/me/profile");
      setMe(data.user || {});
      refresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 1_000_000) { toast.error("Picture must be under 1MB"); return; }
    const reader = new FileReader();
    reader.onload = () => set("profile_picture", reader.result);
    reader.readAsDataURL(f);
  };

  return (
    <div className="grid md:grid-cols-[220px_1fr] gap-8">
      {/* Avatar */}
      <div className="flex flex-col items-center gap-3">
        <Avatar src={form.profile_picture} name={form.name} gender={form.gender} size={140} testId="edit-avatar" />
        <label className="wofy-btn-ghost text-sm cursor-pointer">
          <Camera className="w-4 h-4" />
          {form.profile_picture ? "Change photo" : "Upload / change your profile picture"}
          <input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="edit-avatar-file" />
        </label>
        {form.profile_picture && (
          <button className="text-xs underline opacity-70" onClick={() => set("profile_picture", "")}>Remove photo</button>
        )}
      </div>

      {/* Form */}
      <div className="space-y-6">
        <Field label="Full name *">
          <input className="wofy-input" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="edit-name" />
        </Field>

        <Field label="Handle" hint="/u/your-handle — you can change this once.">
          <div className="flex items-center gap-2">
            <span className="text-xs" style={{ color: "var(--wofy-text-soft)" }}><AtSign className="w-4 h-4 inline" /></span>
            <input className="wofy-input" value={form.handle} onChange={(e) => set("handle", e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))} data-testid="edit-handle" />
          </div>
        </Field>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Gender *" required>
            <select className="wofy-input" value={form.gender} onChange={(e) => set("gender", e.target.value)} data-testid="edit-gender">
              <option value="">Select…</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
              <option value="prefer_not_to_say">Prefer not to say</option>
            </select>
          </Field>
          <Field label="Year of birth" hint="Optional. Keep private if you'd like.">
            <input type="number" min="1900" max={new Date().getFullYear() - 5} className="wofy-input" value={form.year_of_birth} onChange={(e) => set("year_of_birth", e.target.value)} data-testid="edit-yob" />
            <label className="text-xs flex items-center gap-1.5 mt-1 cursor-pointer">
              <input type="checkbox" checked={form.year_of_birth_private} onChange={(e) => set("year_of_birth_private", e.target.checked)} />
              Keep my year of birth private
            </label>
          </Field>
        </div>

        <Field label="Profession / Occupation" hint="Shown on your profile card and in Our Members.">
          <input className="wofy-input" value={form.profession} onChange={(e) => set("profession", e.target.value)} placeholder="Photographer · Nurse · Teacher…" data-testid="edit-profession" />
        </Field>

        <Field label="About / Bio" hint={`Up to 3000 characters. First 250 chars are shown on the summary card. Currently ${form.about.length}/3000.`}>
          <div className="relative">
            <textarea rows={7} maxLength={3000} className="wofy-input" value={form.about} onChange={(e) => set("about", e.target.value)} data-testid="edit-about" placeholder="Tell your story — what drives you, what you're passionate about, and what you'd like people to know about you." />
            <div className="mt-1">
              <AIImproveButton value={form.about} onImproved={(t) => set("about", t)} kind="bio" testId="edit-about-ai" />
            </div>
          </div>
        </Field>

        {/* Socials */}
        <div className="border-t pt-6" style={{ borderColor: "var(--wofy-border)" }}>
          <div className="font-mono text-xs uppercase tracking-widest mb-3">Web & socials</div>
          <div className="grid sm:grid-cols-2 gap-3">
            {SOCIALS.map((s) => (
              <div key={s.key} className="flex items-center gap-2">
                <s.Icon className="w-4 h-4 shrink-0 opacity-70" />
                <input className="wofy-input flex-1" placeholder={`${s.label} URL`} value={form[s.key] || ""} onChange={(e) => set(s.key, e.target.value)} data-testid={`edit-${s.key}`} />
              </div>
            ))}
          </div>
        </div>

        <Field label="General notes / comments" hint="Any additional info you'd like to share. Up to 3000 chars.">
          <div className="relative">
            <textarea rows={4} maxLength={3000} className="wofy-input" value={form.general_notes} onChange={(e) => set("general_notes", e.target.value)} data-testid="edit-notes" />
            <div className="mt-1">
              <AIImproveButton value={form.general_notes} onImproved={(t) => set("general_notes", t)} kind="general" testId="edit-notes-ai" />
            </div>
          </div>
        </Field>

        <div className="flex items-center gap-3">
          <button onClick={save} disabled={busy} className="wofy-btn-primary" data-testid="save-basics-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
          </button>
          {me.handle && (
            <Link to={`/u/${me.handle}`} className="wofy-btn-ghost text-sm">
              View public profile →
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------- LIST SECTIONS ----------------

const LIST_META = {
  education: {
    title: "Education",
    endpoint: "/profile/education",
    fields: [
      { key: "school", label: "School / College / Institution *", type: "text", required: true },
      { key: "area", label: "Areas of study", type: "text" },
      { key: "from_ym", label: "From (YYYY-MM or YYYY)", type: "text", placeholder: "2018-08" },
      { key: "to_ym", label: "To (YYYY-MM or YYYY)", type: "text", placeholder: "2022-05" },
      { key: "website", label: "Website", type: "url" },
      { key: "linkedin", label: "LinkedIn", type: "url" },
      { key: "youtube", label: "YouTube", type: "url" },
      { key: "notes", label: "Notes", type: "textarea", ai: "general" },
    ],
    empty: { school: "" },
  },
  career: {
    title: "Career / Experience",
    endpoint: "/profile/career",
    fields: [
      { key: "name", label: "Project or Organization name *", type: "text", required: true },
      { key: "from_ym", label: "From (YYYY-MM or YYYY)", type: "text", placeholder: "2019-01" },
      { key: "to_ym", label: "To (YYYY-MM or YYYY, or blank = present)", type: "text", placeholder: "Present" },
      { key: "description", label: "Job / Project description", type: "textarea", ai: "job_description" },
      { key: "notes", label: "Notes", type: "textarea", ai: "general" },
    ],
    empty: { name: "" },
  },
  skills: {
    title: "Skills / Talents",
    endpoint: "/profile/skills",
    fields: [
      { key: "name", label: "Skill / Talent name *", type: "text", required: true },
      { key: "description", label: "Description", type: "textarea", ai: "skill_description" },
    ],
    empty: { name: "" },
  },
  achievements: {
    title: "Achievements / Awards",
    endpoint: "/profile/achievements",
    fields: [
      { key: "title", label: "Title of achievement / award *", type: "text", required: true },
      { key: "year", label: "Year *", type: "number", required: true },
      { key: "month", label: "Month (1-12, optional)", type: "number" },
      { key: "description", label: "Description", type: "textarea", ai: "achievement_description" },
    ],
    empty: { title: "", year: new Date().getFullYear() },
  },
};

function ListSection({ kind, me, setMe }) {
  const meta = LIST_META[kind];
  const [items, setItems] = useState(() => Array.isArray(me[kind]) ? me[kind] : []);
  const [busy, setBusy] = useState(false);

  const add = () => setItems((prev) => [...prev, { ...meta.empty, id: `new-${Date.now()}` }]);
  const remove = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));
  const setField = (idx, key, value) => setItems((prev) => prev.map((it, i) => i === idx ? { ...it, [key]: value } : it));

  const save = async () => {
    // Validate required fields
    for (const [i, it] of items.entries()) {
      for (const f of meta.fields) {
        if (f.required && !(it[f.key] || "").toString().trim()) {
          return toast.error(`Entry ${i + 1}: ${f.label.replace(" *", "")} is required.`);
        }
      }
    }
    setBusy(true);
    try {
      const clean = items.map((it) => {
        const out = { ...it };
        if (out.id?.startsWith("new-")) delete out.id;
        if (kind === "achievements") {
          out.year = parseInt(out.year, 10);
          out.month = out.month ? parseInt(out.month, 10) : null;
        }
        return out;
      });
      const { data } = await http.patch(meta.endpoint, { items: clean });
      setItems(data[kind] || clean);
      setMe((m) => ({ ...m, [kind]: data[kind] || clean }));
      toast.success(`${meta.title} saved.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl">{meta.title}</h2>
        <button onClick={add} className="wofy-btn-ghost text-sm" data-testid={`add-${kind}`}>
          <Plus className="w-4 h-4" /> Add entry
        </button>
      </div>

      {items.length === 0 && (
        <div className="wofy-card text-center py-10" style={{ color: "var(--wofy-text-soft)" }}>
          No entries yet. Click <b>Add entry</b> to get started.
        </div>
      )}

      <div className="space-y-4">
        {items.map((it, idx) => (
          <div key={it.id || idx} className="wofy-card !p-5 relative" data-testid={`${kind}-entry-${idx}`}>
            <button onClick={() => remove(idx)} className="absolute top-3 right-3 text-xs inline-flex items-center gap-1 opacity-60 hover:opacity-100 hover:text-red-600" title="Delete this entry">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
            <div className="grid sm:grid-cols-2 gap-3">
              {meta.fields.map((f) => (
                <Field key={f.key} label={f.label} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
                  {f.type === "textarea" ? (
                    <>
                      <textarea rows={3} maxLength={2000} className="wofy-input" placeholder={f.placeholder} value={it[f.key] || ""} onChange={(e) => setField(idx, f.key, e.target.value)} data-testid={`${kind}-${idx}-${f.key}`} />
                      {f.ai && (
                        <div className="mt-1">
                          <AIImproveButton value={it[f.key] || ""} onImproved={(t) => setField(idx, f.key, t)} kind={f.ai} testId={`${kind}-${idx}-${f.key}-ai`} />
                        </div>
                      )}
                    </>
                  ) : (
                    <input type={f.type} placeholder={f.placeholder} className="wofy-input" value={it[f.key] || ""} onChange={(e) => setField(idx, f.key, e.target.value)} data-testid={`${kind}-${idx}-${f.key}`} />
                  )}
                </Field>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6">
        <button onClick={save} disabled={busy} className="wofy-btn-primary" data-testid={`save-${kind}`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save {meta.title.toLowerCase()}
        </button>
      </div>
    </div>
  );
}

// ---------------- PHOTOS ----------------

function PhotosTab({ me, setMe }) {
  const [photos, setPhotos] = useState(me.photos || []);
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (photos.length + files.length > 10) return toast.error("You can only have up to 10 photos.");
    setBusy(true);
    for (const f of files) {
      if (f.size > 5_000_000) { toast.error(`${f.name}: too large (max 5MB)`); continue; }
      try {
        const data_url = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(f); });
        const { data } = await http.post("/profile/photos", { data_url, caption: "", public: true });
        setPhotos((prev) => [...prev, data.photo]);
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Failed to upload ${f.name}`);
      }
    }
    e.target.value = "";
    setBusy(false);
  };

  const togglePublic = async (p) => {
    try {
      const { data } = await http.patch(`/profile/photos/${p.id}`, { public: !p.public });
      setPhotos((prev) => prev.map((x) => x.id === p.id ? data.photo : x));
    } catch (e) { toast.error("Update failed."); }
  };

  const del = async (p) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await http.delete(`/profile/photos/${p.id}`);
      setPhotos((prev) => prev.filter((x) => x.id !== p.id));
    } catch (e) { toast.error("Delete failed."); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl">Photos <span className="text-sm opacity-60">({photos.length}/10)</span></h2>
        <label className="wofy-btn-primary text-sm cursor-pointer">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Upload photos
          <input type="file" accept="image/*" multiple onChange={onFile} className="hidden" data-testid="photos-upload" disabled={busy || photos.length >= 10} />
        </label>
      </div>
      <p className="text-sm mb-4" style={{ color: "var(--wofy-text-soft)" }}>
        Up to 10 photos. Mark each public or private. Only public photos appear on your public profile.
      </p>

      {photos.length === 0 && (
        <div className="wofy-card text-center py-10" style={{ color: "var(--wofy-text-soft)" }}>
          No photos yet. Click <b>Upload photos</b> above.
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {photos.map((p) => (
          <div key={p.id} className="wofy-card !p-0 overflow-hidden" data-testid={`photo-${p.id}`}>
            <div className="aspect-square bg-black">
              <img src={p.url ? (p.url.startsWith("http") ? p.url : `${API}${p.url}`) : ""} alt={p.caption || "Photo"} className="w-full h-full object-cover" />
            </div>
            <div className="p-2 flex items-center justify-between text-xs">
              <label className="inline-flex items-center gap-1 cursor-pointer">
                <input type="checkbox" checked={p.public} onChange={() => togglePublic(p)} data-testid={`photo-${p.id}-public`} />
                Public
              </label>
              <button onClick={() => del(p)} className="text-red-600 opacity-70 hover:opacity-100"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------- PRIVACY ----------------

function PrivacyTab({ me, setMe }) {
  const [visibility, setVisibility] = useState(me.profile_visibility || "public");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await http.patch("/profile/extended", { profile_visibility: visibility });
      setMe((m) => ({ ...m, profile_visibility: visibility }));
      toast.success("Privacy updated.");
    } catch (e) { toast.error("Update failed."); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl">
      <h2 className="font-display text-2xl mb-4">Profile privacy</h2>
      <div className="space-y-4">
        <label className="flex items-start gap-3 wofy-card !p-4 cursor-pointer" style={visibility === "public" ? { boxShadow: "0 0 0 2px var(--wofy-primary)" } : {}}>
          <input type="radio" checked={visibility === "public"} onChange={() => setVisibility("public")} className="mt-1" data-testid="privacy-public" />
          <div>
            <div className="font-semibold">Public — anyone can view your full profile</div>
            <div className="text-sm mt-1" style={{ color: "var(--wofy-text-soft)" }}>Your name, photo, bio, education, career, skills, achievements and public photos are visible to any visitor.</div>
          </div>
        </label>
        <label className="flex items-start gap-3 wofy-card !p-4 cursor-pointer" style={visibility === "private" ? { boxShadow: "0 0 0 2px var(--wofy-primary)" } : {}}>
          <input type="radio" checked={visibility === "private"} onChange={() => setVisibility("private")} className="mt-1" data-testid="privacy-private" />
          <div>
            <div className="font-semibold">Private — show a limited card only</div>
            <div className="text-sm mt-1" style={{ color: "var(--wofy-text-soft)" }}>
              Visitors see just your name, photo, location, profession, and a 1-line bio preview, plus a <b>Request to connect</b> button. Full details are hidden.
            </div>
          </div>
        </label>
      </div>
      <div className="mt-6">
        <button onClick={save} disabled={busy} className="wofy-btn-primary" data-testid="save-privacy">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
        </button>
      </div>
    </div>
  );
}

// ---------------- shared ----------------

function Field({ label, hint, children, required, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-xs font-mono uppercase tracking-widest mb-1.5" style={{ color: "var(--wofy-text-soft)" }}>
        {label}{required && <span style={{ color: "#dc2626" }}> *</span>}
      </span>
      {children}
      {hint && <span className="block text-xs mt-1 opacity-70">{hint}</span>}
    </label>
  );
}
